// SPDX-License-Identifier: BUSL-1.1

/**
 * Sidebar navigation with governance-focused section groups.
 *
 * Renders nav items grouped by domain (Overview, Config Governance, Agent
 * Governance, Observability) with active-state highlighting, lucide icons,
 * and optional badge counts per item.
 *
 */

import {
  type LucideIcon,
  LayoutDashboard,
  FileCode,
  ToggleLeft,
  Bot,
  Inbox,
  ScrollText,
  Users,
  KeyIcon,
  Activity,
} from 'lucide-react';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { safeCount } from '@cli-ceo/shared-constants';
import type { Agent } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  label: string;
  items: ReadonlyArray<NavItem>;
}

interface NavBadge {
  count: number;
  variant: 'red' | 'amber' | 'blue';
}

/* ------------------------------------------------------------------ */
/*  Navigation structure                                               */
/* ------------------------------------------------------------------ */

const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Config Governance',
    items: [
      { to: '/configs', label: 'Configs', icon: FileCode },
      { to: '/flags', label: 'Feature Flags', icon: ToggleLeft },
    ],
  },
  {
    label: 'Agent Governance',
    items: [
      { to: '/agents', label: 'Agents', icon: Bot },
      { to: '/inbox', label: 'Moderation Inbox', icon: Inbox },
    ],
  },
  {
    label: 'Operations',
    items: [
      // CEO-V4 P1 — fleet grid of every active agent + last-activity-at.
      { to: '/operators', label: 'Operators', icon: Activity },
    ],
  },
  {
    label: 'Observability',
    items: [
      { to: '/audit', label: 'Audit Log', icon: ScrollText },
      { to: '/teams', label: 'Teams', icon: Users },
      { to: '/keys', label: 'API Keys', icon: KeyIcon },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Badge rendering                                                    */
/* ------------------------------------------------------------------ */

function NavBadgePill({ badge }: { badge: NavBadge }) {
  if (badge.count <= 0) return null;

  const colorClass =
    badge.variant === 'red'
      ? 'bg-red-900/30 text-red-400'
      : badge.variant === 'amber'
        ? 'bg-amber-900/30 text-amber-400'
        : 'bg-blue-900/30 text-blue-400';

  return (
    <span
      className={`ml-auto rounded px-1.5 font-mono text-[10px] font-semibold ${colorClass}`}
    >
      {badge.count}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Hooks                                                              */
/* ------------------------------------------------------------------ */

function useSidebarBadges(): Record<string, NavBadge | undefined> {
  const { data: agentsResponse } = useQuery({
    queryKey: ['agents'],
    queryFn: () => apiFetch<{ data: Agent[] }>('/agents'),
    staleTime: 30_000,
    retry: 1,
  });

  const { data: pendingCount } = useQuery({
    queryKey: ['moderation', 'pending-count'],
    queryFn: () => apiFetch<{ count: number }>('/moderation/pending-count'),
    staleTime: 15_000,
    retry: 1,
  });

  // NocoDB #4600: Use safeCount to prevent SPA crash if agentsResponse.data is not an array
  const failedAgentCount = safeCount(agentsResponse?.data, (a) => a.status === 'error');

  return {
    '/agents': { count: failedAgentCount, variant: 'red' },
    '/inbox': { count: pendingCount?.count ?? 0, variant: 'blue' },
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SidebarNav() {
  const badges = useSidebarBadges();

  return (
    <nav className="flex flex-col">
      {NAV_GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            {group.label}
          </div>
          {group.items.map(({ to, label, icon: Icon }) => {
            const badge = badges[to];
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 rounded px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? 'bg-neutral-800 text-white'
                      : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200'
                  }`
                }
              >
                <Icon className="size-4" />
                <span>{label}</span>
                {badge ? <NavBadgePill badge={badge} /> : null}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
